import { useMemo } from 'react'

import { Option } from '@joor/design-system'
import { useSelector } from 'react-redux'

import {
  BillingAddress,
  BrandRetailerPaymentMethod,
  BrandRetailerShippingMethod,
  Geography,
  Location,
  OrderBilling,
  ShippingAddress,
  Tax,
} from '__generated__/atlas-types'

import { formatNameWithCode } from 'utils/formatters'

import { isAccountTypeBrand } from 'store/currentUser/selectors'

import {
  useBrandShippingBillingInfo,
  useCountries,
  useLiteRetailerAddresses,
} from '../EditAddressModal.queries'
import {
  OrderBillingShippingData,
  useListConnectionDoors,
  useOrderBillingShipping,
} from 'features/Orders/Review/components/Overview/components/AddressesWidget/addressesWidget.queries'

export type RetailerLocation = Partial<Location>

export type RetailerInfo = {
  primaryLocation?: RetailerLocation
  locations?: RetailerLocation[]
  email?: string | null
}

export type AddressModalData = {
  countries?: Pick<Geography, 'id' | 'name' | 'taxLabel'>[]
  billingAddresses?: BillingAddress[]
  shippingAddresses?: ShippingAddress[]
  doorOptions?: Option[]
  shippingMethodOptions?: Option[]
  paymentMethodOptions?: Option[]
  orderBilling?: OrderBilling
  orderShipping?: OrderBillingShippingData['shipping']
  orderTax?: Tax | null
  loadingAddressModalData: boolean
  retailerInfo?: RetailerInfo
}

export const useAddressModalData = (
  brandId?: string,
  retailerId?: string,
): AddressModalData => {
  const isBrand = useSelector(isAccountTypeBrand)
  const { brand, loadingBrandInfo } = useBrandShippingBillingInfo(
    brandId,
    retailerId,
  )
  const { geographies, loadingGeographies } = useCountries()
  const {
    data: retailerLocationsQueryData,
    loading: loadingRetailerAddresses,
  } = useLiteRetailerAddresses(retailerId)

  const retailerInfo: RetailerInfo = useMemo(() => {
    const retailerLocations = retailerLocationsQueryData?.retailer?.locations
    const email =
      retailerLocationsQueryData?.retailer?.publicRetailerProfile?.email

    const primaryLocation = retailerLocations?.find(
      (location: any) => location?.isPrimaryLocation,
    )

    const locations = !isBrand
      ? retailerLocations?.filter(
          (location: any) => !location.isPrimaryLocation,
        )
      : []

    return {
      primaryLocation,
      locations,
      email,
    }
  }, [retailerLocationsQueryData, isBrand])

  const countries = useMemo(
    () => geographies?.edges.map((edge) => edge.node),
    [geographies?.edges],
  )

  const { orderBillingShippingData, loading: loadingOrderBillingShippingData } =
    useOrderBillingShipping()

  const billingAddresses = useMemo(
    () =>
      brand?.connectedAccounts.edges
        .filter((edge: any) => edge.billingAddresses.length)
        .flatMap((edge: any) => edge.billingAddresses),
    [brand?.connectedAccounts.edges],
  )

  const shippingAddresses = useMemo(
    () =>
      brand?.connectedAccounts.edges
        .filter((edge: any) => edge.shippingAddresses.length)
        .flatMap((edge: any) => edge.shippingAddresses),
    [brand?.connectedAccounts.edges],
  )

  const { doors, loading: loadingDoors } = useListConnectionDoors({
    retailerId,
    isBrand,
  })

  const doorOptions: Option[] = useMemo(
    () =>
      doors?.map((door: any) => ({
        value: door.id,
        label: formatNameWithCode(door.name, door.code),
      })) ?? [],
    [doors],
  )

  const shippingMethodOptions: Option[] = useMemo(
    () =>
      brand?.connectedAccounts.edges[0]?.shippingMethods?.map(
        ({ shippingMethod }: BrandRetailerShippingMethod) => ({
          value: shippingMethod?.id!,
          label: formatNameWithCode(shippingMethod?.name, shippingMethod?.code),
        }),
      ) ?? [],
    [brand],
  )

  const paymentMethodOptions: Option[] = useMemo(
    () =>
      brand?.connectedAccounts.edges[0]?.paymentMethods?.map(
        ({ paymentMethod }: BrandRetailerPaymentMethod) => ({
          value: paymentMethod.id,
          label: formatNameWithCode(paymentMethod.name, paymentMethod.code),
        }),
      ) ?? [],
    [brand],
  )

  return {
    countries,
    billingAddresses,
    retailerInfo,
    shippingAddresses,
    doorOptions,
    shippingMethodOptions,
    paymentMethodOptions,
    orderBilling: orderBillingShippingData?.billing,
    orderShipping: orderBillingShippingData?.shipping,
    orderTax: orderBillingShippingData?.tax,
    loadingAddressModalData:
      loadingBrandInfo ||
      loadingGeographies ||
      loadingOrderBillingShippingData ||
      loadingRetailerAddresses ||
      loadingDoors,
  }
}
